import Section from '@hero/ui-kit/layout/Section';
import MarkModal from '../../../components/MarkModal';
import { MarkButton, MarkHeading } from '../../../components/MarkControls';
import React from 'react';
import P from '@hero/ui-kit/typography/P';
import type { DripStep } from '../../../types';
type Props = {
    stepId: string;
    allSteps: DripStep[];
    entryStepId: string | null;
    onConfirm: () => void;
    onCancel: () => void;
    onJumpToStep?: (stepId: string) => void;
};
type IncomingRef = {
    sourceId: string;
    field: string;
};
const findIncomingRefs = (stepId: string, steps: DripStep[]): IncomingRef[] => {
    const refs: IncomingRef[] = [];
    for (const s of steps) {
        if (s.id === stepId) continue;
        switch (s.type) {
            case 'check_segment':
                if (s.on_true === stepId) refs.push({ sourceId: s.id, field: 'on_true' });
                if (s.on_false === stepId) refs.push({ sourceId: s.id, field: 'on_false' });
                break;
            case 'activity':
                if (s.next === stepId) refs.push({ sourceId: s.id, field: 'next' });
                break;
            case 'ab_split':
                if (s.arms.A === stepId) refs.push({ sourceId: s.id, field: 'arms.A' });
                if (s.arms.B === stepId) refs.push({ sourceId: s.id, field: 'arms.B' });
                break;
        }
    }
    return refs;
};
const DeleteStepConfirm: React.FC<Props> = ({
    stepId,
    allSteps,
    entryStepId,
    onConfirm,
    onCancel,
    onJumpToStep
}) => {
    const incoming = findIncomingRefs(stepId, allSteps);
    const blocked = incoming.length > 0;
    // Mirrors useDripDraft.removeStep's fallback: the first remaining step.
    const isEntry = entryStepId === stepId;
    const nextEntryId = isEntry ? (allSteps.find((s) => s.id !== stepId)?.id ?? null) : null;
    return (
        <MarkModal
            onClose={onCancel}
            isPending={false}
            aria-label={`Delete step ${stepId}`}
            styles={{
                width: 'min(64rem, 90vw)'
            }}
        >
            <Section noDefaultPadding>
                <MarkHeading
                    role="h5"
                    tabIndex={-1}
                    style={{
                        margin: '0 0 1rem'
                    }}
                >
                    Delete step "{stepId}"?
                </MarkHeading>
                {blocked ? (
                    <>
                        <P
                            noDefaultMargin
                            size="small"
                            styles={{
                                color: 'var(--mark-danger)',
                                marginBottom: '0.8rem'
                            }}
                        >
                            This step is still referenced by other steps. Clear these references
                            first:
                        </P>
                        <ul
                            style={{
                                margin: '0 0 1rem',
                                paddingLeft: '1.4rem'
                            }}
                        >
                            {incoming.map((r) => (
                                <li key={`${r.sourceId}-${r.field}`}>
                                    {onJumpToStep ? (
                                        <MarkButton
                                            type="button"
                                            onClick={() => {
                                                onCancel();
                                                onJumpToStep(r.sourceId);
                                            }}
                                        >
                                            Step "{r.sourceId}" → {r.field}
                                        </MarkButton>
                                    ) : (
                                        <>
                                            Step "{r.sourceId}" → {r.field}
                                        </>
                                    )}
                                </li>
                            ))}
                        </ul>
                    </>
                ) : (
                    <>
                        {isEntry && (
                            <P
                                noDefaultMargin
                                size="small"
                                strong
                                styles={{
                                    color: 'var(--mark-warning)',
                                    marginBottom: '0.8rem'
                                }}
                            >
                                {nextEntryId
                                    ? `This is the entry step. "${nextEntryId}" will become the new entry step — check it (or pick another) before saving.`
                                    : 'This is the entry step and the only step — the drip will have no steps left.'}
                            </P>
                        )}
                        <P
                            noDefaultMargin
                            size="small"
                            styles={{
                                marginBottom: '1rem'
                            }}
                        >
                            This action removes the step from the draft. Save to persist.
                        </P>
                    </>
                )}
                <div
                    style={{
                        display: 'flex',
                        gap: '0.8rem',
                        justifyContent: 'flex-end'
                    }}
                >
                    <MarkButton type="button" onClick={onCancel}>
                        Cancel
                    </MarkButton>
                    <MarkButton type="button" onClick={onConfirm} disabled={blocked}>
                        Delete
                    </MarkButton>
                </div>
            </Section>
        </MarkModal>
    );
};
export default DeleteStepConfirm;
